import { ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const portals = [
  { key: "gst", name: "GST Portal", url: "https://www.gst.gov.in", desc: "File GSTR-1, GSTR-3B, GSTR-9. Manage GSTIN registration, refunds, notices.", tag: "GST" },
  { key: "itr", name: "Income Tax e-Filing", url: "https://www.incometax.gov.in", desc: "File ITR, view 26AS, AIS/TIS, respond to notices, e-pay tax challans.", tag: "ITR" },
  { key: "traces", name: "TRACES (TDS)", url: "https://www.tdscpc.gov.in", desc: "TDS/TCS reconciliation, Form 16/16A, defaults, Justification Reports.", tag: "TDS" },
  { key: "mca", name: "MCA / ROC Portal", url: "https://www.mca.gov.in", desc: "Company filings: AOC-4, MGT-7, DIR-3 KYC, DSC, name reservation.", tag: "ROC" },
  { key: "ewaybill", name: "E-Way Bill System", url: "https://ewaybillgst.gov.in", desc: "Generate, cancel and verify E-Way Bills for goods movement.", tag: "GST" },
  { key: "einvoice", name: "E-Invoice Portal (IRN)", url: "https://einvoice1.gst.gov.in", desc: "Generate IRN, QR codes; bulk upload e-invoices.", tag: "GST" },
  { key: "epfo", name: "EPFO Employer Portal", url: "https://unifiedportal-emp.epfindia.gov.in", desc: "Provident Fund returns (ECR), member KYC, transfer claims.", tag: "Payroll" },
  { key: "esic", name: "ESIC Portal", url: "https://www.esic.gov.in", desc: "Employee State Insurance contributions, returns, IP details.", tag: "Payroll" },
  { key: "udin", name: "ICAI UDIN Portal", url: "https://udin.icai.org", desc: "Generate Unique Document Identification Number for audit / certificates.", tag: "ICAI" },
  { key: "icai", name: "ICAI Member Portal", url: "https://www.icai.org", desc: "Membership, CPE hours, firm registration, notifications.", tag: "ICAI" },
  { key: "dgft", name: "DGFT Portal", url: "https://www.dgft.gov.in", desc: "IEC code, RoDTEP, exports / imports compliance.", tag: "Trade" },
  { key: "fssai", name: "FSSAI FoSCoS", url: "https://foscos.fssai.gov.in", desc: "Food licence registration, renewal, returns.", tag: "Other" },
];

const tagColor = {
  GST: "border-[#002FA7] text-[#002FA7]",
  ITR: "border-zinc-900 text-zinc-900",
  TDS: "border-[#F79009] text-[#B54708]",
  ROC: "border-[#7C3AED] text-[#7C3AED]",
  Payroll: "border-[#039855] text-[#027A48]",
  ICAI: "border-zinc-900 text-zinc-900",
  Trade: "border-[#D92D20] text-[#B42318]",
  Other: "border-zinc-400 text-zinc-600",
};

export default function Portals() {
  const open = (url) => window.open(url, "_blank", "noopener,noreferrer");
  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px]">
      <div className="mb-8">
        <p className="label-caps mb-2">Automation</p>
        <h1 className="font-display font-black text-4xl tracking-tighter">Tax Portals</h1>
        <p className="text-zinc-600 mt-2">One-click access to every Indian compliance portal.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {portals.map(p => (
          <div key={p.key} className="bg-white border border-zinc-200 p-5 hover:border-zinc-900 transition-colors group" data-testid={`portal-${p.key}`}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-display font-bold text-lg tracking-tight">{p.name}</h3>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${tagColor[p.tag]} bg-white`}>{p.tag}</span>
            </div>
            <p className="text-sm text-zinc-600 mb-4 leading-relaxed min-h-[60px]">{p.desc}</p>
            <div className="font-mono text-xs text-zinc-500 mb-4 truncate">{p.url}</div>
            <div className="flex gap-2">
              <button onClick={() => open(p.url)} data-testid={`open-${p.key}`} className="flex-1 bg-zinc-900 text-white px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-zinc-800 active:translate-y-[1px] transition-all">
                <ExternalLink size={14} /> Open
              </button>
              <button onClick={() => copy(p.url)} className="border border-zinc-300 px-3 py-2 hover:bg-zinc-50" title="Copy link">
                <Copy size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Mapping used elsewhere (e.g. Tax Returns row → quick launch portal)
export const portalForReturnType = (type) => {
  if (!type) return null;
  const t = type.toUpperCase();
  if (t.startsWith("GSTR") || t.includes("E-WAY")) return portals.find(p => p.key === "gst");
  if (t.startsWith("ITR") || t.includes("ADVANCE TAX")) return portals.find(p => p.key === "itr");
  if (t.startsWith("TDS") || t.startsWith("FORM 16")) return portals.find(p => p.key === "traces");
  if (t.startsWith("ROC") || t.includes("AOC") || t.includes("MGT")) return portals.find(p => p.key === "mca");
  if (t.includes("AUDIT")) return portals.find(p => p.key === "itr");
  return null;
};
